import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { DATE_FORMATS } from "../../constants";

dayjs.extend(customParseFormat);

/** True when app runs against production (IST offset applied on display). */
export function isRefundModuleProductionEnv(): boolean {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";
  const isProductionByUrl =
    apiBaseUrl.includes("services.ridenimbo.com") ||
    apiBaseUrl.includes("ridenimbo.com");

  return (
    process.env.REACT_APP_ENV === "production" ||
    process.env.NODE_ENV === "production" ||
    isProductionByUrl
  );
}

/** Same pattern as `DATE_FORMATS.formatDateTime` — e.g. `Apr 30 2026, 08:19 PM`. */
function formatWallClockDateTime(date: Date): string {
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const year = date.getFullYear();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHours = (hours % 12 || 12).toString().padStart(2, "0");
  const displayMinutes = minutes.toString().padStart(2, "0");
  return `${month} ${day} ${year}, ${displayHours}:${displayMinutes} ${ampm}`;
}

/**
 * Parses API timestamps without shifting wall-clock (QA/UAT/dev).
 * ISO strings without `Z` use date/time digits from the string as-is.
 */
function parseRefundTimestampPreserveWallClock(raw: string): Date | null {
  const s = raw.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/.exec(s);
  if (isoMatch) {
    const [, y, mo, d, h, mi, sec] = isoMatch;
    const parsed = dayjs(
      `${y}-${mo}-${d} ${h}:${mi}:${sec}`,
      "YYYY-MM-DD HH:mm:ss",
      true,
    );
    if (parsed.isValid()) return parsed.toDate();
  }
  if (/^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/.test(s)) {
    const parsed = dayjs(s, "MM/DD/YYYY HH:mm:ss", true);
    if (parsed.isValid()) return parsed.toDate();
  }
  const parsed = dayjs(s);
  if (parsed.isValid()) return parsed.toDate();
  try {
    const fallback = new Date(s);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  } catch {
    return null;
  }
}

/**
 * Refund-module date/time display (`Apr 30 2026, 08:19 PM`).
 * Production: IST adjustment via `DATE_FORMATS.formatDateTime`.
 * QA / UAT / dev: same layout, wall-clock from API (no +5:30).
 */
export function formatRefundDateTime(
  dateString: string | null | undefined,
): string {
  const raw = dateString != null ? String(dateString).trim() : "";
  if (!raw) return "";

  if (isRefundModuleProductionEnv()) {
    return DATE_FORMATS.formatDateTime(raw);
  }

  const date = parseRefundTimestampPreserveWallClock(raw);
  if (!date) return raw;
  return formatWallClockDateTime(date);
}
